/**
 * HeadPoseOnnx — onnxruntime-web inference for the distilled head-pose model
 * (`headpose_student.onnx`, MobileNetV3-small, 0.32MB). Trained by distilling
 * 6DRepNet360 on 300W_LP (GT) + VAT (teacher-labelled) crops with augmentation.
 *
 * On the VAT gaze-target benchmark it matches the 6DRepNet360 teacher on the
 * horizontal (left/right) gaze-zone axis (82.3% vs 82.7%) and beats the
 * deployed MediaPipe/SixDRepNet baseline by ~+10pt — at 1/290th the size.
 * Validation: laeo_eval/VAT_RESULTS.md.
 *
 * It runs on a head crop and returns yaw/pitch in DEGREES. It is OFF by default
 * in the app (`?headPoseOnnx=on`) and fails gracefully (returns last/empty).
 *
 * Input  : [1,3,128,128] RGB, ImageNet-normalised.
 * Output : [1,6] = [sin(yaw),cos(yaw),sin(pitch),cos(pitch),sin(roll),cos(roll)].
 *
 * SIGN NOTE: the model's yaw/pitch sign follows the 300W-LP convention, which
 * may differ from the live MediaPipe convention (yaw+ = user's right, pitch+ =
 * up). `?hpFlipYaw` / `?hpFlipPitch` flip each axis for on-device calibration.
 */
import * as ort from 'onnxruntime-web';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const ortRt = require('onnxruntime-web/wasm') as typeof ort;

const ASSET_BASE = '/static';
ortRt.env.wasm.wasmPaths = `${ASSET_BASE}/`;
ortRt.env.wasm.numThreads = 1;
ortRt.env.wasm.simd = true;
ortRt.env.wasm.proxy = false;
ortRt.env.logLevel = 'warning';

const MODEL_URL = `${ASSET_BASE}/headpose_student.onnx`;
const IMGSZ = 128;
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

export interface HeadPose { yaw: number; pitch: number; roll: number }
export type HeadPoseStatus = 'idle' | 'loading' | 'ready' | 'failed';

let modelStatus: HeadPoseStatus = 'idle';
let modelError: string | null = null;
export function getHeadPoseStatus(): { status: HeadPoseStatus; error: string | null } {
  return { status: modelStatus, error: modelError };
}

let sessionPromise: Promise<ort.InferenceSession> | null = null;
function loadSession(): Promise<ort.InferenceSession> {
  if (!sessionPromise) {
    modelStatus = 'loading';
    sessionPromise = ortRt.InferenceSession.create(MODEL_URL, {
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all',
    }).then(s => {
      modelStatus = 'ready';
      // eslint-disable-next-line no-console
      console.log('[HeadPoseOnnx] loaded', s.inputNames, '→', s.outputNames);
      return s;
    }).catch(err => {
      modelStatus = 'failed';
      modelError = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.warn('[HeadPoseOnnx] model not loaded (place headpose_student.onnx):', modelError);
      throw err;
    });
  }
  return sessionPromise;
}

let inflight = false;
let lastResult: HeadPose | null = null;
export function getHeadPoseSync(): HeadPose | null { return lastResult; }

/** Normalised head bbox {x,y,w,h} in [0,1]. */
export interface BBoxNorm { x: number; y: number; w: number; h: number }

/**
 * Single-flight async head-pose on the given normalised head bbox of the video
 * frame. Returns the freshest result immediately and kicks off a new inference
 * when idle, so it never blocks the render loop.
 */
export async function detectHeadPose(
  video: HTMLVideoElement,
  bbox: BBoxNorm,
  offCanvas: HTMLCanvasElement,
): Promise<HeadPose | null> {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return lastResult;
  if (inflight) return lastResult;
  inflight = true;
  try {
    const session = await loadSession();
    // crop head bbox (clamped) → square IMGSZ
    const cx = (bbox.x + bbox.w / 2) * vw;
    const cy = (bbox.y + bbox.h / 2) * vh;
    const side = Math.max(bbox.w * vw, bbox.h * vh);
    const sx = Math.max(0, cx - side / 2);
    const sy = Math.max(0, cy - side / 2);
    const sw = Math.min(vw - sx, side);
    const sh = Math.min(vh - sy, side);
    if (sw < 8 || sh < 8) return lastResult;
    if (offCanvas.width !== IMGSZ || offCanvas.height !== IMGSZ) {
      offCanvas.width = IMGSZ; offCanvas.height = IMGSZ;
    }
    const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return lastResult;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, IMGSZ, IMGSZ);
    const img = ctx.getImageData(0, 0, IMGSZ, IMGSZ).data;

    const n = IMGSZ * IMGSZ;
    const chw = new Float32Array(3 * n);
    for (let i = 0; i < n; i++) {
      chw[i] = (img[i * 4] / 255 - MEAN[0]) / STD[0];
      chw[n + i] = (img[i * 4 + 1] / 255 - MEAN[1]) / STD[1];
      chw[2 * n + i] = (img[i * 4 + 2] / 255 - MEAN[2]) / STD[2];
    }
    const input = new ortRt.Tensor('float32', chw, [1, 3, IMGSZ, IMGSZ]);
    const out = await session.run({ [session.inputNames[0]]: input });
    const t = out[session.outputNames[0]].data as Float32Array;
    // [sin(yaw),cos(yaw),sin(pitch),cos(pitch),sin(roll),cos(roll)]
    const deg = (s: number, c: number) => (Math.atan2(s, c) * 180) / Math.PI;
    lastResult = { yaw: deg(t[0], t[1]), pitch: deg(t[2], t[3]), roll: deg(t[4], t[5]) };
    return lastResult;
  } catch {
    return lastResult;
  } finally {
    inflight = false;
  }
}
